<template>
  <InputYearly
    v-model="profiles"
    :num_years="num_years"
    :label="label"
    :suffix="suffix"
    :hint="`${hint ? hint + '. ' : ''}${
      computedItemsType === 'profile' ? 'Enter a name to upload a new profile' : ''
    }`"
    type="number"
    :chart="profileDygraph"
    :table-input="!toggleItemsType && computedItemsType === 'constant'"
    :hide-label="hideLabel"
    :spread-values="spreadValues"
    :label-size="labelSize"
    :fill="fill"
    :fill-value="fillValue"
    :extra-button="toggleItemsType"
    :nullable="nullable"
    :start-year="startYear"
    :repeat.sync="repeat"
    @open-chart="queryProfiles = true"
    @close-chart="queryProfiles = false"
  >
    <template #extraButton>
      <div class="d-flex">
        <v-btn-toggle v-if="toggleItemsType" v-model="computedItemsType" color="primary">
          <v-tooltip top>
            <template #activator="{ on }">
              <v-btn small icon value="constant" v-on="on">
                <v-icon color="primary">mdi-numeric</v-icon>
              </v-btn>
            </template>
            <span>Constant values</span>
          </v-tooltip>
          <v-tooltip top>
            <template #activator="{ on }">
              <v-btn small icon value="profile" v-on="on">
                <v-icon color="primary">mdi-chart-timeline-variant</v-icon>
              </v-btn>
            </template>
            <span>Profiles</span>
          </v-tooltip>
        </v-btn-toggle>
        <DialogForm v-if="multiYearUpload" :submit="submitMultiyear" @click.stop>
          <template #activator="{ on: dialogOn }">
            <v-tooltip top>
              <template #activator="{ on: tooltipOn }">
                <v-btn icon color="primary" v-on="{ ...tooltipOn, ...dialogOn }">
                  <v-icon>publish</v-icon>
                </v-btn>
              </template>
              <span> Upload multiyear profile </span>
            </v-tooltip>
          </template>
          <template #default>
            <InputCSV :on-complete="saveMultiyear" @file-selected="setCSVName($event, 'CSVName')">
              <template #activator="{ triggerUpload }">
                <v-text-field
                  v-model="CSVName"
                  label="Profile"
                  placeholder="Upload csv file"
                  readonly
                  append-icon="publish"
                  @click="triggerUpload"
                  @click:append="triggerUpload"
                >
                </v-text-field>
              </template>
            </InputCSV>
          </template>
        </DialogForm>
      </div>
    </template>

    <template
      #default="{ value: val, year, label }"
      v-if="(!nullable || (nullable && value)) && computedItemsType === 'profile'"
    >
      <InputProfile
        v-model="val[year - 1]"
        :profile-type="profileType"
        :suffix="suffix"
        :label="label"
        :object-value="objectValue"
        @input="setYearValue($event, year - 1)"
        @change="$emit('change')"
      ></InputProfile>
    </template>
  </InputYearly>
</template>

<script>
import { makeFindMixin } from "feathers-vuex";
import { mapActions } from "vuex";

import ChartYearly from "@/components/charts/ChartYearly.vue";
import ChartLine from "@/components/charts/ChartLine.vue";

export default {
  name: "InputYearlyProfile",
  components: {
    DialogForm: () => import("@/components/DialogForm.vue"),
    InputCSV: () => import("@/components/InputCSV.vue"),
    InputYearly: () => import("@/components/InputYearly.vue"),
    InputProfile: () => import("@/components/InputProfile.vue"),
  },
  mixins: [
    makeFindMixin({
      service: "profiles",
      name: "profilesValues",
      watch: "params",
      queryWhen: "queryProfiles",
    }),
  ],
  props: {
    spreadValues: { type: Boolean, required: false, default: false },
    profileType: {
      type: String,
      required: false,
      default: "",
    },
    num_years: {
      type: Number,
      required: true,
    },
    value: {
      type: Array,
      required: false,
      default() {
        return [];
      },
    },
    label: {
      type: String,
      required: false,
      default: "Profile",
    },
    hideLabel: {
      type: Boolean,
      required: false,
      defalut: false,
    },
    labelSize: {
      type: Number,
      required: false,
      default: 4,
    },
    title: {
      type: String,
      required: false,
      default: "Profile",
    },
    suffix: {
      type: String,
      required: false,
      default: "",
    },
    scaleProfile: {
      type: Function,
      required: false,
      default: null,
    },
    hint: {
      type: String,
      required: false,
      default: null,
    },
    fill: {
      type: Boolean,
      required: false,
      default: false,
    },
    fillValue: {
      type: Number,
      required: false,
      default: null,
    },
    multiYearUpload: {
      type: Boolean,
      required: false,
      default: false,
    },
    nullable: {
      type: Boolean,
      required: false,
      default: false,
    },
    objectValue: {
      type: Boolean,
      required: false,
      default: false,
    },
    itemsType: {
      type: String,
      required: false,
      default: "profile",
      validator: (v) => ["profile", "constant"].includes(v),
    },
    toggleItemsType: {
      type: Boolean,
      required: false,
      default: false,
    },
    startYear: {
      type: [Number, null],
      required: false,
      default: null,
    },
  },
  data() {
    return {
      CSVName: "",
      multiyearCSV: [],
      queryProfiles: false,
      repeat: false,
      localItemsType: "profile",
    };
  },
  computed: {
    profiles: {
      get() {
        if (this.objectValue && this.value) {
          return this.value.map((v) => v[this.computedItemsType === "profile" ? "id" : "value"]);
        }
        return this.value;
      },
      set(val) {
        let valueCopy = val;
        if (this.objectValue && valueCopy) {
          valueCopy = val.map((v) =>
            this.computedItemsType === "profile"
              ? { _resource: "PROFILE", id: v }
              : { _resource: "CONSTANT", value: v }
          );
        }
        this.$emit("input", valueCopy);
        this.$emit("change");
      },
    },
    profilesValuesParams() {
      return this.value && this.computedItemsType === "profile"
        ? {
            query: {
              type: this.profileType,
              id: {
                $in: this.objectValue ? this.value.map((v) => v.id) : this.value,
              },
              $select: ["id", "type", "name", ...(this.queryProfiles ? ["value"] : [])],
            },
          }
        : {};
    },
    profileDygraph() {
      return this.computedItemsType === "profile"
        ? {
            title: this.title,
            component: ChartYearly,
            props: {
              loading: this.isFindProfilesValuesPending,
              chartComponent: ChartLine,
              data: this.chartData,
              allowDownloadData: true,
              startYear: this.startYear,
              options: {
                legend: "always",
                ylabel: "Demand (MW)",
                xlabel: "Hour",
                showRangeSelector: true,
              },
            },
          }
        : null;
    },
    chartData() {
      if (this.value && this.computedItemsType === "profile") {
        const ids = this.objectValue ? this.value.map((v) => v.id) : this.value;
        let values = this.profilesValues.filter((v) => v.value && ids.includes(v.id));
        if (!values.length && this.queryProfiles) {
          if (this.profilesValuesLatestQuery) {
            values = this.profilesValuesLatestQuery.response.filter(
              (v) => v.value && ids.includes(v.id)
            );
          }
        }
        const chartData = this.value.map((e, year) => {
          let data = e;
          let profileName = null;
          let profile;
          if (Number.isInteger(data)) {
            profile = values.find((v) => v.id === data);
          } else if (this.objectValue) {
            profile = values.find((v) => v.id === data.id);
          }
          if (!profile || !profile.value || !this.$utils.getIfExists(profile, ["value"])) {
            return null;
          }
          data = profile.value;
          profileName = profile.name;

          if (this.scaleProfile) {
            data = this.scaleProfile(data, year);
          }
          return {
            name: profileName,
            value: data,
          };
        });
        return chartData;
      }
      return { name: "", value: [] };
    },
    computedItemsType: {
      get() {
        if (this.toggleItemsType) {
          return this.localItemsType;
        }
        return this.itemsType;
      },
      set(v) {
        this.localItemsType = v;
        this.$emit("update:items-type", v);
      },
    },
  },
  created() {
    if (this.toggleItemsType) {
      // eslint-disable-next-line no-underscore-dangle
      if (this.value && this.value[0] && this.value[0]._resource === "CONSTANT") {
        this.localItemsType = "constant";
      } else {
        this.localItemsType = "profile";
      }
    }
  },
  methods: {
    ...mapActions({
      createProfile: "profiles/create",
    }),
    saveMultiyear(results) {
      const years = results.reduce(
        (acc, value) =>
          acc.includes(value.date.toISOString().slice(0, 4))
            ? acc
            : [...acc, value.date.toISOString().slice(0, 4)],
        []
      );
      const parsedResults = years.map((year) =>
        results
          .filter((value) => value.date.toISOString().slice(0, 4) === year)
          .map((value) => ({ ...value, date: value.date.toISOString() }))
      );
      this.multiyearCSV = parsedResults;
    },
    setCSVName($event, name) {
      this[name] = $event.name;
    },
    submitMultiyear() {
      const promises = [];
      this.multiyearCSV.forEach((yearArray, i) => {
        promises.push(
          this.createProfile({
            type: this.profileType,
            name: `${this.CSVName}_${i + 1}`,
            value: yearArray,
          }).then((res) => res)
        );
      });
      return Promise.all(promises).then((values) => {
        const valueCopy = this.value;
        valueCopy.splice(
          0,
          values.length,
          ...values.map((v) => (this.objectValue ? { _resource: "PROFILE", id: v.id } : v.id))
        );
        this.$emit("input", valueCopy);
        this.$emit("change");
      });
    },
    setYearValue($event, index = null) {
      if (this.repeat) {
        const valueCopy = Array(this.value.length).fill($event);
        this.$emit("input", valueCopy);
        this.$emit("change");
      } else if (this.objectValue && $event.id) {
        if (!this.value) {
          this.$emit("input", Array(this.num_years).fill(null));
        }
        const valueCopy = this.value;
        valueCopy.splice(index, 1, $event);
        this.$emit("input", valueCopy);
        this.$emit("change");
      }
    },
  },
};
</script>

<style></style>
